// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { OrderStatus } from '@shared/utils/Trading/OrderStatus';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { Instrument } from './cache/Instrument';
import { Order } from './cache/Order';
import { Position, ServerCalculation } from './cache/Position';
import { MathUtils } from '@shared/utils/MathUtils';
export class Trading {
    constructor(dataCache) {
        this.dataCache = dataCache;
        this.OrderDict = new Map();
        this.PositionDict = new Map();
        this.PositionDictCorporateAction = new Map();
    }
    clear() {
        this.OrderDict = new Map();
        this.PositionDict = new Map();
        this.PositionDictCorporateAction = new Map();
    }
    HandleOpenOrderMessageDirect(msg) {
        this.TryRemoveFilledOrder(msg) || this.TryAddOrderFromMessage(msg) || this.TryUpdateOrderFromMessage(msg);
    }
    HandleCancelOrderMessageDirect(msg) {
        const orderId = msg.OrderId;
        const order = this.OrderDict.get(orderId);
        if (!order)
            return;
        order.Status = msg.OrderStatus;
        order.Comment = msg.Comment;
        if (msg.LastAmount != null) {
            // hsa: I am not sure about it
            order.Amount = msg.LastAmount;
        }
        else if (msg.Quantity != null) {
            order.Amount = msg.Quantity;
        }
        if (msg.DisClosedQuantity != null) {
            order.DisclosedQty = msg.DisClosedQuantity;
        }
        if (msg.SharesFilled != null) {
            order.SharesFilled = msg.SharesFilled;
        }
        if (msg.CancelTime != null) {
            order.UTCDateTime = msg.CancelTime;
        }
        // Call update order event?
        this.TryRemoveOrder(orderId);
    }
    TryRemoveOrder(orderNumber) {
        if (!orderNumber)
            return false;
        const order = this.OrderDict.get(orderNumber);
        if (!order)
            return;
        this.TryRemoveClosingOrderFromPosition(order);
        order.Dispose();
        this.OrderDict.delete(orderNumber);
        this.dataCache.OnRemoveOrder.Raise(order);
    }
    TryRemoveFilledOrder(openOrderMessage) {
        if (openOrderMessage.OrderStatus === OrderStatus.REMOVED) {
            const removedOrder = this.getOrderById(openOrderMessage.OrderNumber);
            let removedPosition = false;
            if (removedOrder) {
                this.TryRemoveOrder(removedOrder.OrderNumber);
            }
            else {
                this.dataCache.RemovedTradeHistory(openOrderMessage.OrderNumber);
                this.dataCache.OnRemovedTradeEvent.Raise(openOrderMessage.OrderNumber);
                removedPosition = true;
            }
            return !!removedOrder || removedPosition;
        }
        if (openOrderMessage.Quantity <= 0 || openOrderMessage.Quantity !== openOrderMessage.SharesFilled) {
            return false;
        }
        // ордер полностью заполнен - удаляем
        const filledOrder = this.getOrderById(openOrderMessage.OrderNumber);
        if (filledOrder == null) {
            return true;
        }
        filledOrder.LastUpdateTime = new Date(openOrderMessage.LastUpdateTime);
        if (openOrderMessage.OrderStatus !== null) {
            filledOrder.Status = openOrderMessage.OrderStatus;
            if ((openOrderMessage.OrderStatus === OrderStatus.FILLED ||
                openOrderMessage.OrderStatus === OrderStatus.PART_FILLED) &&
                isValidNumber(openOrderMessage.LastPrice)) {
                filledOrder.Price = openOrderMessage.LastPrice;
            }
        }
        this.TryRemoveOrder(filledOrder.OrderNumber);
        return true;
    }
    TryAddOrderFromMessage(msg) {
        const orderKey = msg.OrderNumber;
        if (this.OrderDict.has(orderKey)) {
            return false;
        }
        const instrument = this.dataCache.getInstrumentByTradable_ID(msg.InstrumentTradableID, msg.Route);
        const account = this.dataCache.GetAccountById(msg.Account);
        if (!instrument /* || !account) */) {
            this.dataCache.AddDeferredMessage(msg);
            return false;
        }
        if (!account) {
            return false;
        }
        const order = new Order(this.dataCache, this.dataCache.FQuoteCache, {
            OrderNumber: msg.OrderNumber,
            OrderType: msg.OrderType,
            Amount: msg.Quantity,
            Price: msg.Price,
            BuySell: msg.BuySell,
            TimeInForce: msg.TimeInForce,
            UTCDateTime: msg.UTCDateTime,
            Active: msg.Active,
            Route: msg.Route,
            Account: account,
            Instrument: instrument,
            ProductType: msg.ProductType,
            DisclosedQty: msg.DisClosedQuantity,
            Leverage: msg.Leverage,
            ServerCalculation: null
        });
        if (msg.StopLimit != null) {
            order.StopLimit = msg.StopLimit;
        }
        order.ExpireAt = msg.ExpireTime;
        order.SharesFilled = msg.SharesFilled;
        order.AverageFilledPrice = msg.AverageFilledPrice;
        order.OrderGroupId = msg.OrderGroupId;
        order.PositionId = msg.PositionId;
        order.Exchange = msg.Exchange;
        order.TrStopOffset = msg.TrStopOffset;
        order.BoundTo = msg.BoundToOrderId;
        if (msg.OrderStatus !== null) {
            order.Status = msg.OrderStatus;
        }
        order.RealActive = msg.RealActive;
        order.TakeProfitPriceValue = msg.TakeProfitPriceValue;
        order.StopLossPriceValue = msg.StopLossPriceValue;
        order.StopLossLimitPriceValue = Number.isNaN(order.StopLossPriceValue) ? null : msg.StopLossLimit;
        order.TakeProfitPriceType = msg.TakeProfitPriceType;
        order.StopLossPriceType = msg.StopLossPriceType;
        order.TriggerSL = msg.TriggerSL;
        order.TriggerTP = msg.TriggerTP;
        order.TriggerSLTP = msg.TriggerSLTP;
        order.Replaceable = msg.Replaceable;
        order.ExternalOrderId = msg.ExternalOrderId;
        order.ExternalStatus = msg.ExternalStatus;
        this.OrderDict.set(orderKey, order);
        order.IsActivated = msg.IsActivated;
        order.Comment = msg.Comment;
        order.UserComment = msg.UserComment;
        order.StrategyId = msg.StrategyId;
        if (msg.ExecutionType !== null) {
            order.ExecutionType = msg.ExecutionType;
        }
        order.IsAllowOTHTrading = msg.IsAllowOTHTrading;
        if (order.Active) {
            this.dataCache.OnAddOrder.Raise(order);
        }
        else {
            this.TryAssignClosingOrderToPosition(order);
        }
        return true;
    }
    TryUpdateOrderFromMessage(msg) {
        const order = this.OrderDict.get(msg.OrderNumber);
        if (!order)
            return false;
        if (msg.Quantity != null) {
            order.Amount = msg.Quantity;
        }
        if (msg.DisClosedQuantity != null) {
            order.DisclosedQty = msg.DisClosedQuantity;
        }
        if (msg.TimeInForce != null) {
            order.TimeInForce = msg.TimeInForce;
        }
        if (msg.Price != null) {
            order.Price = msg.Price;
        }
        if (msg.OrderType != null) {
            order.OrderType = msg.OrderType;
        }
        if (msg.UTCDateTime != null) {
            order.UTCDateTime = msg.UTCDateTime;
        }
        if (msg.LastUpdateTime != null) {
            order.LastUpdateTime = msg.LastUpdateTime;
        }
        if (msg.SharesFilled != null) {
            order.SharesFilled = msg.SharesFilled;
        }
        if (msg.AverageFilledPrice != null) {
            order.AverageFilledPrice = msg.AverageFilledPrice;
        }
        if (msg.ExpireTime != null) {
            order.ExpireAt = msg.ExpireTime;
        }
        if (msg.TrStopOffset != null) {
            order.TrStopOffset = msg.TrStopOffset;
        }
        if (msg.StopLimit != null) {
            order.StopLimit = msg.StopLimit;
        }
        if (msg.TakeProfitPriceValue != null) {
            order.TakeProfitPriceValue = msg.TakeProfitPriceValue;
        }
        if (msg.StopLossPriceValue != null) {
            order.StopLossPriceValue = msg.StopLossPriceValue;
        }
        order.StopLossLimitPriceValue = Number.isNaN(order.StopLossPriceValue) ? null : msg.StopLossLimit;
        if (msg.TakeProfitPriceType != null) {
            order.TakeProfitPriceType = msg.TakeProfitPriceType;
        }
        if (msg.StopLossPriceType != null) {
            order.StopLossPriceType = msg.StopLossPriceType;
        }
        // if (msg.TriggerSL != null)   // можна скинути існуючі тригери видаляючи SL/TP у ордера
        order.TriggerSL = msg.TriggerSL;
        // if (msg.TriggerTP != null)
        order.TriggerTP = msg.TriggerTP;
        // if (msg.TriggerSLTP != null)
        order.TriggerSLTP = msg.TriggerSLTP;
        if (msg.ExternalOrderId != null) {
            order.ExternalOrderId = msg.ExternalOrderId;
        }
        if (msg.ExternalStatus != null) {
            order.ExternalStatus = msg.ExternalStatus;
        }
        if (msg.PositionId != null) {
            order.PositionId = msg.PositionId;
        }
        if (msg.BoundToOrderId != null) {
            order.BoundTo = msg.BoundToOrderId;
        }
        if (msg.OrderStatus !== null) {
            order.Status = msg.OrderStatus;
        }
        if (msg.Leverage) {
            order.Leverage = msg.Leverage;
        }
        if (msg.StrategyId !== null) {
            order.StrategyId = msg.StrategyId;
        }
        order.IsActivated = msg.IsActivated;
        if (msg.ExecutionType !== null) {
            order.ExecutionType = msg.ExecutionType;
        }
        if (isValidString(msg.Comment)) {
            order.Comment = msg.Comment;
        }
        if (isValidString(msg.UserComment)) {
            order.UserComment = msg.UserComment;
        }
        if (order.IsActivated) {
            this.dataCache.OnActivateOrder.Raise(order);
        }
        order.IsAllowOTHTrading = msg.IsAllowOTHTrading;
        if (order.Active) {
            this.dataCache.OnUpdateOrder.Raise(order);
        }
        else {
            this.TryAssignClosingOrderToPosition(order);
        }
        return true;
    }
    HandleClosePositionMessageDirect(message) {
        const id = message.Id;
        this.RemovePosition(id);
    }
    RemovePosition(id) {
        var _a, _b;
        const closedPosition = (_b = (_a = this.PositionDict.get(id)) !== null && _a !== void 0 ? _a : this.PositionDictCorporateAction.get(id)) !== null && _b !== void 0 ? _b : null;
        if (!closedPosition)
            return;
        const cpabc = closedPosition.Account.BaseCurrency;
        const slOrder = closedPosition.SLOrder;
        if (slOrder)
            this.TryRemoveOrder(slOrder.OrderNumber);
        const tpOrder = closedPosition.TPOrder;
        if (tpOrder)
            this.TryRemoveOrder(tpOrder.OrderNumber);
        closedPosition.Dispose();
        if (closedPosition.ProductType === ProductType.CorporateAction) {
            this.PositionDictCorporateAction.delete(id);
            this.dataCache.OnRemoveCorporateAction.Raise(closedPosition);
        }
        else {
            this.PositionDict.delete(id);
            this.dataCache.OnRemovePosition.Raise(closedPosition);
        }
        if (cpabc !== this.dataCache.userBaseCurrency) {
            this.dataCache.userBaseCurrency = this.dataCache.getUserBaseCurrency();
        }
    }
    HandlePositionMessageDirect(message) {
        this.TryAddPositionFromMessage(message) || this.TryUpdatePositionFromMessage(message);
    }
    TryAddPositionFromMessage(message) {
        var _a, _b;
        const positionDict = this.PositionDict;
        const positionDictCorporateAction = this.PositionDictCorporateAction;
        let position = (_b = (_a = positionDict.get(message.PositionId)) !== null && _a !== void 0 ? _a : positionDictCorporateAction.get(message.PositionId)) !== null && _b !== void 0 ? _b : null;
        if (message.Amount === 0 && position && this.dataCache.Loaded && message.ProductTypeModifiedAmount !== null) {
            this.dataCache.FOrderExecutor.createEventMessageProductTypeModified(position);
        } //, message.ProductTypeModifiedAmount)
        if (position)
            return false;
        // https://tp.traderevolution.com/entity/80132
        if (message.Amount === 0) {
            return false;
        }
        const instrument = this.dataCache.getInstrumentByTradable_ID(message.InstrumentTradableID, message.Route);
        const account = this.dataCache.GetAccountById(message.Account);
        if (!instrument /* || !account) */) {
            this.dataCache.AddDeferredMessage(message);
            return false;
        }
        if (!account) {
            return false;
        }
        position = new Position(this.dataCache, this.dataCache.FQuoteCache, {
            PositionId: message.PositionId,
            SuperPositionId: message.SuperPositionId, // (FIFO) #80010
            Account: account,
            Instrument: instrument,
            Amount: message.Amount,
            OpenPrice: message.OpenPrice,
            OperationType: message.OperationType,
            OpenTime: message.OpenTime,
            OpenOrderId: message.OpenOrderId,
            Route: message.Route,
            RealizedPL: message.RealizedPL,
            ProductType: message.ProductType,
            Leverage: message.Leverage,
            AccruedInterest: message.AccruedInterest,
            ServerCalculation: new ServerCalculation(message.ServerCalculationCurPriceClose, message.ServerCalculationExposure, message.ServerCalculationPositionValue, message.ServerCalculationNetPL, message.ServerCalculationGrossPL)
        });
        position.OpenCrossPrice = message.OpenCrossPrice;
        position.Commission = message.Commission;
        position.Swaps = message.Swaps;
        position.StartOfDayAmount = message.StartOfDayAmount;
        position.OptionExerciseStatus = message.OptionExerciseStatus;
        position.CorporateActionType = message.CorporateActionType;
        position.Comment = message.Comment;
        position.StrategyId = message.StrategyId;
        if (position.ProductType === ProductType.CorporateAction) {
            positionDictCorporateAction.set(message.PositionId, position);
        }
        else {
            positionDict.set(message.PositionId, position);
        }
        if (positionDict.size === 1) {
            this.dataCache.userBaseCurrency = this.dataCache.getUserBaseCurrency();
        }
        const pabc = position.Account.BaseCurrency;
        if (pabc !== this.dataCache.userBaseCurrency) {
            this.dataCache.userBaseCurrency = this.dataCache.baseCurrency;
        }
        position.RecalcAmounts();
        if (position.ProductType === ProductType.CorporateAction) {
            this.dataCache.OnAddCorporateAction.Raise(position);
        }
        else {
            this.dataCache.OnAddPosition.Raise(position);
        }
        if (this.dataCache.Loaded && message.ProductTypeModifiedAmount !== null) {
            this.dataCache.FOrderExecutor.createEventMessageProductTypeModified(position, message.ProductTypeModifiedAmount);
        }
        return true;
    }
    TryUpdatePositionFromMessage(message) {
        var _a, _b;
        const positionDict = this.PositionDict;
        const positionDictCorporateAction = this.PositionDictCorporateAction;
        const position = (_b = (_a = positionDict.get(message.PositionId)) !== null && _a !== void 0 ? _a : positionDictCorporateAction.get(message.PositionId)) !== null && _b !== void 0 ? _b : null;
        if (!position)
            return false;
        if (message.Amount === 0) {
            this.RemovePosition(message.PositionId);
            return true;
        }
        position.UpdateByMessage(message);
        position.RecalcAmounts();
        if (position.ProductType === ProductType.CorporateAction) {
            this.dataCache.OnUpdateCorporateAction.Raise(position);
        }
        else {
            this.dataCache.OnUpdatePosition.Raise(position);
        }
        if (this.dataCache.Loaded && message.ProductTypeModifiedAmount !== null) {
            this.dataCache.FOrderExecutor.createEventMessageProductTypeModified(position, message.ProductTypeModifiedAmount);
        }
        return true;
    }
    getAllOrders() {
        return Array.from(this.OrderDict.values());
    }
    getOrderById(id) {
        var _a;
        return (_a = this.OrderDict.get(id)) !== null && _a !== void 0 ? _a : null;
    }
    collectByIds(ids, getter) {
        const result = [];
        for (let i = 0, len = ids.length; i < len; i++) {
            const item = getter(ids[i]);
            if (!isNullOrUndefined(item)) {
                result.push(item);
            }
        }
        return result;
    }
    filterValues(values, predicate) {
        const result = [];
        for (const item of values) {
            if (predicate(item)) {
                result.push(item);
            }
        }
        return result;
    }
    getOrdersById(idArray) {
        return this.collectByIds(idArray, (id) => this.getOrderById(id));
    }
    getOrdersByAccount(account) {
        if (!account)
            return [];
        const accId = account.BstrAccount;
        return this.filterValues(this.OrderDict.values(), (order) => order.Account.BstrAccount === accId);
    }
    getOrdersByInstrument(instrument) {
        if (!instrument)
            return [];
        const insId = instrument.GetInteriorID();
        return this.filterValues(this.OrderDict.values(), (order) => order.Instrument.GetInteriorID() === insId);
    }
    getOrdersBySide(side) {
        return this.filterValues(this.OrderDict.values(), (order) => order.BuySell === side);
    }
    getPositionsBySide(side) {
        return this.filterValues(this.PositionDict.values(), (pos) => pos.BuySell === side);
    }
    getPositionsByNetPL(isPositive) {
        return this.filterValues(this.PositionDict.values(), (pos) => (pos.PnLCalculator.NetPL > 0) === isPositive);
    }
    getOrdersByTIF(tif) {
        return this.filterValues(this.OrderDict.values(), (order) => order.TimeInForce === tif);
    }
    getOrdersByType(orderType) {
        return this.filterValues(this.OrderDict.values(), (order) => order.OrderType === orderType);
    }
    getOrdersByInstrumentAndAccount(instrument, account) {
        if (!instrument || !account) {
            return [];
        }
        return this.filterValues(this.OrderDict.values(), (order) => Instrument.IsEqualInstrument(order.Instrument, instrument) && order.Account === account);
    }
    getPositionById(id) {
        var _a;
        return (_a = this.PositionDict.get(id)) !== null && _a !== void 0 ? _a : null;
    }
    getPositionsCorporateActionById(id) {
        var _a;
        return (_a = this.PositionDictCorporateAction.get(id)) !== null && _a !== void 0 ? _a : null;
    }
    getAllPositions() {
        return Array.from(this.PositionDict.values());
    }
    getAllPositionIds() {
        return Array.from(this.PositionDict.keys());
    }
    getAllPositionsCorporateAction() {
        return Array.from(this.PositionDictCorporateAction.values());
    }
    getPositionsById(idArray) {
        return this.collectByIds(idArray, (id) => this.getPositionById(id));
    }
    getPositionsByAccount(account, isCorporateAction = false) {
        if (isNullOrUndefined(account)) {
            return [];
        }
        const posDict = isCorporateAction ? this.PositionDictCorporateAction : this.PositionDict;
        const accId = account.BstrAccount;
        return this.filterValues(posDict.values(), (pos) => pos.Account.BstrAccount === accId);
    }
    getPositionsByInstrument(instrument, isCorporateAction = false) {
        if (!instrument) {
            return [];
        }
        const posDict = isCorporateAction ? this.PositionDictCorporateAction : this.PositionDict;
        const insId = instrument.GetInteriorID();
        return this.filterValues(posDict.values(), (pos) => pos.Instrument.GetInteriorID() === insId);
    }
    getPositionsByInstrumentAndAccount(instrument, account) {
        if (!instrument || !account) {
            return [];
        }
        return this.filterValues(this.PositionDict.values(), (pos) => pos.Instrument === instrument && pos.Account === account);
    }
    PositionListForAccountInstrument(instrument, account) {
        if (instrument === null || account === null) {
            return [];
        }
        return this.filterValues(this.PositionDict.values(), (position) => position.Account === account &&
            Instrument.IsEqualInstrument(position.Instrument, instrument) &&
            position.Instrument.Route === instrument.Route);
    }
    OrdersListForAccountInstrument(instrument, account, buySell = null, addSLTP = true) {
        if (instrument == null || account == null) {
            return [];
        }
        return this.filterValues(this.OrderDict.values(), (order) => order.Account === account &&
            Instrument.IsEqualInstrument(order.Instrument, instrument) &&
            order.Instrument.Route === instrument.Route &&
            order.Active);
    }
    getClosestOrdersBySideAndPrice(instrument, account, side, targetPrice) {
        if (!instrument || !account || !Number.isFinite(targetPrice)) {
            return [];
        }
        const orders = this.getOrdersByInstrumentAndAccount(instrument, account).filter((order) => order.BuySell === side && Number.isFinite(order.Price));
        if (!isValidArray(orders)) {
            return [];
        }
        const result = [];
        let minDiff = Number.POSITIVE_INFINITY;
        const epsilon = MathUtils.MATH_ROUND_EPSILON;
        for (const order of orders) {
            const diff = Math.abs(order.Price - targetPrice);
            if (diff < minDiff - epsilon) {
                result.length = 0;
                result.push(order);
                minDiff = diff;
            }
            else if (Math.abs(diff - minDiff) <= epsilon) {
                result.push(order);
            }
        }
        return result;
    }
    getBindToOrderIdArray(srcOrder) {
        return this.filterValues(this.OrderDict.values(), (bindToOrder) => Trading.canBindTo(srcOrder, bindToOrder)).map((bindToOrder) => bindToOrder.OrderNumber);
    }
    FindParentForOrder(order) {
        var _a;
        if (!order)
            return null;
        if (!order.PositionId)
            return null;
        return (_a = this.PositionDict.get(order.PositionId)) !== null && _a !== void 0 ? _a : null;
    }
    TryAssignClosingOrderToPosition(order) {
        if (!order)
            return false;
        const pos = this.FindParentForOrder(order);
        if (!pos)
            return false;
        const orderType = order.OrderType;
        const OrderTypes = OrderType;
        const isSL = orderType === OrderTypes.Stop ||
            orderType === OrderTypes.StopLimit ||
            orderType === OrderTypes.TrailingStop;
        if (isSL) {
            pos.SLOrder = order;
            this.dataCache.OnAddSLOrderToPosition.Raise(pos);
            return true;
        }
        const isTP = orderType === OrderTypes.Limit;
        if (isTP) {
            pos.TPOrder = order;
            this.dataCache.OnAddTPOrderToPosition.Raise(pos);
            return true;
        }
        const isMarket = orderType === OrderTypes.Market; // #94085
        if (isMarket) {
            this.dataCache.OnAddOrder.Raise(order); // #94085
            return true;
        }
        return false;
    }
    TryRemoveClosingOrderFromPosition(order) {
        if (!order)
            return false;
        const pos = this.FindParentForOrder(order);
        if (!pos)
            return false;
        const orderNumber = order.OrderNumber;
        const slOrder = pos.SLOrder;
        if ((slOrder === null || slOrder === void 0 ? void 0 : slOrder.OrderNumber) === orderNumber) {
            this.dataCache.OnRemoveSLOrderFromPosition.Raise(pos);
            pos.SLOrder = null;
            return true;
        }
        const tpOrder = pos.TPOrder;
        if ((tpOrder === null || tpOrder === void 0 ? void 0 : tpOrder.OrderNumber) === orderNumber) {
            this.dataCache.OnRemoveTPOrderFromPosition.Raise(pos);
            pos.TPOrder = null;
            return true;
        }
        return false;
    }
    IsSLTP(order) {
        return !order.Active || (order.BoundTo != null && this.getPositionByBindTo(order.BoundTo) != null);
    }
    getPositionByBindTo(BoundTo) {
        var _a;
        if (!BoundTo || BoundTo == '-1') {
            return null;
        }
        /// Почему я не использую getPositionByOpenOrderId вместо этого метода
        /// 1. чтобы не сломать логику поиска ордеров/позиций
        /// 2. тут positions[] отрабатывает быстрее чем getPositionByOpenOrderId, а у нас большинство поз - мульти.
        const pos = (_a = this.PositionDict.get(BoundTo)) !== null && _a !== void 0 ? _a : null;
        return pos;
    }
    static canBindTo(srcOrder, bindToOrder) {
        if (isNullOrUndefined(srcOrder) ||
            isNullOrUndefined(bindToOrder) ||
            srcOrder.Instrument !== bindToOrder.Instrument ||
            srcOrder.Account !== bindToOrder.Account ||
            srcOrder.SharesFilled !== 0 ||
            bindToOrder.SharesFilled !== 0 ||
            !srcOrder.Active ||
            !bindToOrder.Active) {
            return false;
        }
        const srcOrdType = srcOrder.OrderType;
        const bindToOrdType = bindToOrder.OrderType;
        if (srcOrdType === OrderType.Market || bindToOrdType === OrderType.Market) {
            return false;
        }
        const srcBuy = srcOrder.BuySell;
        const bindToBuy = bindToOrder.BuySell;
        const isSrcLimit = OrderUtils.isOrderLimit(srcOrdType);
        const isSrcStop = OrderUtils.isOrderStop(srcOrdType);
        const isBindToLimit = OrderUtils.isOrderLimit(bindToOrdType);
        const isBindToStop = OrderUtils.isOrderStop(bindToOrdType);
        // Ордера с одинаковым типом, но с разным направлением можно линковать.
        if (srcOrdType === bindToOrdType) {
            return srcBuy !== bindToBuy;
        }
        // Ордера с разным типом и с одинаковым направлением можно линковать,
        // тип имеет значение.
        return (srcBuy === bindToBuy &&
            // один должен быть limit, а второй или Stop, или StopLimit, или TrailingStop
            ((isSrcLimit && isBindToStop) || (isSrcStop && isBindToLimit)));
    }
}
